import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import logo from "../assets/logo.png"; 

// Icons
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Flag,
  CheckCircle,
  Video,
  Grid,
  AlertTriangle,
  ShieldCheck,
  AlertCircle
} from "lucide-react";

/**
 * ExamSession
 * Update: On Strike 3, we force a 2-second countdown and REMOVE the return button.
 * This forces the standard "timer expired" submission logic to trigger, ensuring reliability.
 */

function ExamSession() {
  const { attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const precheck = location.state || {};

  // exam state
  const [exam, setExam] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); 
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [loading, setLoading] = useState(true);

  // submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // UI state
  const [showConfirm, setShowConfirm] = useState(false); 
  const [leavePromptOpen, setLeavePromptOpen] = useState(false); 
  const [banner, setBanner] = useState(null);
  const [bannerType, setBannerType] = useState("");

  // camera/proctor states
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // fullscreen warning & strike tracking
  const [fullscreenWarning, setFullscreenWarning] = useState(false);
  const [fullscreenSecondsLeft, setFullscreenSecondsLeft] = useState(10); 
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0); 
  const fullscreenTimerRef = useRef(null);

  // refs for timers & submit
  const timersRef = useRef({ mainTimer: null });
  const submitRef = useRef(null);

  // proctor log queue if attemptId not ready
  const proctorLogQueueRef = useRef([]);

  // IMPORTANT: allow/deny cleanup flag
  const safeToCleanupRef = useRef(true);

  // --- LOGIC START ---
  const logProctor = async (type, message) => {
    try {
      if (!attemptId) {
        proctorLogQueueRef.current.push({ type, message });
        return;
      }
      await api.post(`/api/attempts/${attemptId}/proctor-event`, {
        type,
        message
      });
    } catch (err) {
      console.error("Proctor log error:", err.response?.data || err.message);
    }
  };

  // Robust Beacon replacement using fetch + keepalive
  const sendBeaconSubmit = (payload) => {
    try {
      const url = `${window.location.origin}/api/attempts/${attemptId}/submit`;
      const token = localStorage.getItem("token"); 
      
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload),
        keepalive: true 
      }).catch(err => console.error("Beacon fetch failed", err));

      return true;
    } catch (e) {
      console.error("Beacon error", e);
      return false;
    }
  };

  const cleanupMediaAndFullscreen = useCallback(() => {
    if (timersRef.current.mainTimer) {
      clearInterval(timersRef.current.mainTimer);
      timersRef.current.mainTimer = null;
    }
    if (fullscreenTimerRef.current) {
      clearInterval(fullscreenTimerRef.current);
      fullscreenTimerRef.current = null;
    }
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch (_) {}
      streamRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
      } catch (_) {}
    }
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setFullscreenWarning(false);
    setFullscreenSecondsLeft(10);
  }, []);

  const handleSubmit = useCallback(
    async (auto = false) => {
      if (submitting || submitted) return submitted;

      setSubmitting(true);

      const payload = {
        autoSubmit: !!auto,
        responses: questions.map((q) => ({
          questionId: q._id,
          selectedOptionKey: answers[q._id]?.selectedOptionKey || null
        }))
      };

      try {
        // 1. Try Primary API Call
        await api.post(`/api/attempts/${attemptId}/submit`, payload);

        cleanupMediaAndFullscreen();
        setBanner(auto ? "Security violation or Time over. Exam submitted." : "Exam submitted successfully.");
        setBannerType("success");
        setSubmitted(true);
        setSubmitting(false);

        navigate("/dashboard?tab=my", { replace: true });
        return true;
      } catch (err) {
        console.error("Submit error:", err.response?.data || err.message);

        // 2. Fallback: Use Robust Fetch-Keepalive
        try {
          sendBeaconSubmit(payload); 
          cleanupMediaAndFullscreen();
          setBanner("Submission sent via backup method.");
          setBannerType("success");
          setSubmitted(true);
          setSubmitting(false);
          
          setTimeout(() => {
             navigate("/dashboard?tab=my", { replace: true });
          }, 100);
          return true;
        } catch (_) {}

        setBanner(err.response?.data?.message || "Failed to submit.");
        setBannerType("error");
        setSubmitting(false);
        return false;
      }
    },
    [attemptId, answers, questions, submitting, submitted, cleanupMediaAndFullscreen, navigate]
  );

  useEffect(() => {
    submitRef.current = handleSubmit;
  }, [handleSubmit]);

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    if (video.srcObject !== stream) {
      video.srcObject = stream;
      video.play().catch((err) => console.warn("Video play error (force attach):", err));
    }
  });

  // --- INITIALIZATION & EVENT LISTENERS ---
  useEffect(() => {
    let mounted = true;
    const initExam = async () => {
      try {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false
          });
          streamRef.current = stream;

          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch((err) => console.warn("Video play error:", err));
          }
          setCameraError(null);
        } catch (e) {
          console.warn("Exam video error:", e);
          setCameraError("Camera preview not available. Please keep camera permissions on.");
        }

        const res = await api.post(`/api/attempts/${attemptId}/start`, {
          cameraCheckPassed: !!precheck.cameraOk,
          micCheckPassed: !!precheck.micOk,
          networkCheckPassed: !!precheck.networkOk
        });

        if (!mounted) return;
        const { attempt: attemptRes, exam: examRes, questions: qRes } = res.data;
        setAttempt(attemptRes);
        setExam(examRes);
        setQuestions(qRes || []);
        setSecondsLeft((examRes?.durationMinutes || 0) * 60);
        setLoading(false);

        if (proctorLogQueueRef.current.length > 0) {
          proctorLogQueueRef.current.forEach((entry) => logProctor(entry.type, entry.message));
          proctorLogQueueRef.current = [];
        }
      } catch (err) {
        console.error("Start attempt error:", err.response?.data || err.message);
        setBanner(err.response?.data?.message || "Unable to start exam. It may already be submitted or expired.");
        setBannerType("error");
        cleanupMediaAndFullscreen();
        navigate("/dashboard?tab=my", { replace: true });
      }
    };

    initExam();

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        logProctor("TAB_SWITCH", "User switched tab or minimized window");
      }
    };

    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      if (!isFullscreen) {
        setFullscreenExitCount((prev) => prev + 1);
      } else {
        // User returned to fullscreen
        // NOTE: If user returns on strike 3, we DO NOT cancel. 
        // Logic handled in strike effect below.
        if (fullscreenExitCount < 3) {
            setFullscreenWarning(false);
            setFullscreenSecondsLeft(10);
            if (fullscreenTimerRef.current) {
              clearInterval(fullscreenTimerRef.current);
              fullscreenTimerRef.current = null;
            }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", handleVisibility);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);

      if (safeToCleanupRef.current) {
        cleanupMediaAndFullscreen();
      } else {
        if (fullscreenTimerRef.current) {
          clearInterval(fullscreenTimerRef.current);
          fullscreenTimerRef.current = null;
        }
      }
    };
  }, [attemptId]); // Removed fullscreenExitCount dependency here

  // --- UPDATED STRIKE LOGIC HANDLER ---
  useEffect(() => {
    if (fullscreenExitCount === 0) return;

    const performStrikeLogic = async () => {
      logProctor("FULLSCREEN_EXIT", `Fullscreen Exit Detected. Strike ${fullscreenExitCount}/3`);

      // Always clear old timer first
      if (fullscreenTimerRef.current) {
        clearInterval(fullscreenTimerRef.current);
        fullscreenTimerRef.current = null;
      }

      setFullscreenWarning(true);

      // STRATEGY: 
      // Strike 1-2: 10 seconds, can return.
      // Strike 3+: 2 seconds, CANNOT return (UI hides button).
      const newTime = fullscreenExitCount >= 3 ? 2 : 10;
      setFullscreenSecondsLeft(newTime);

      fullscreenTimerRef.current = setInterval(() => {
        setFullscreenSecondsLeft((s) => {
          if (s <= 1) {
            // Timer expired
            clearInterval(fullscreenTimerRef.current);
            fullscreenTimerRef.current = null;
            setFullscreenWarning(false);
            
            // Trigger Submit
            (async () => {
              if (submitRef.current) await submitRef.current(true);
            })();
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    };

    performStrikeLogic();
    
    // Cleanup on unmount or if strike count changes again (rare)
    return () => {
        if (fullscreenTimerRef.current) {
             clearInterval(fullscreenTimerRef.current);
             fullscreenTimerRef.current = null;
        }
    };
  }, [fullscreenExitCount]);

  // --- MAIN TIMER LOGIC ---
  useEffect(() => {
    if (secondsLeft == null) return;

    if (timersRef.current.mainTimer) {
      clearInterval(timersRef.current.mainTimer);
      timersRef.current.mainTimer = null;
    }

    if (secondsLeft <= 0) {
      (async () => {
        try {
          if (submitRef.current) await submitRef.current(true);
        } catch (_) {}
      })();
      return;
    }

    timersRef.current.mainTimer = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timersRef.current.mainTimer);
          timersRef.current.mainTimer = null;
          (async () => {
            try {
              if (submitRef.current) await submitRef.current(true);
            } catch (_) {}
          })();
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => {
      if (timersRef.current.mainTimer) {
        clearInterval(timersRef.current.mainTimer);
        timersRef.current.mainTimer = null;
      }
    };
  }, [secondsLeft]);

  const questionAreaRef = useRef(null);
  useEffect(() => {
    const el = questionAreaRef.current;
    if (!el) return;
    const handleContextMenu = (e) => e.preventDefault();
    const handleCopy = (e) => e.preventDefault();
    el.addEventListener("contextmenu", handleContextMenu);
    el.addEventListener("copy", handleCopy);
    return () => {
      el.removeEventListener("contextmenu", handleContextMenu);
      el.removeEventListener("copy", handleCopy);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      try {
        const payload = {
          autoSubmit: true,
          responses: questions.map((q) => ({
            questionId: q._id,
            selectedOptionKey: answers[q._id]?.selectedOptionKey || null
          }))
        };
        sendBeaconSubmit(payload);
      } catch (_) {}
      e.preventDefault();
      e.returnValue = "Leaving submits the exam.";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [questions, answers]);

  useEffect(() => {
    const token = `exam-${attemptId}-${Date.now()}`;
    try {
      window.history.pushState({ token }, document.title);
    } catch (_) {}

    const handlePopState = (e) => {
      safeToCleanupRef.current = false;
      setLeavePromptOpen(true);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (safeToCleanupRef.current) {
        cleanupMediaAndFullscreen();
      } else {
        if (timersRef.current.mainTimer) {
          clearInterval(timersRef.current.mainTimer);
          timersRef.current.mainTimer = null;
        }
        if (fullscreenTimerRef.current) {
          clearInterval(fullscreenTimerRef.current);
          fullscreenTimerRef.current = null;
        }
      }
    };
  }, [attemptId]);

  const stayOnExam = () => {
    safeToCleanupRef.current = true;
    setLeavePromptOpen(false);
    try {
      window.history.pushState({}, document.title);
    } catch (_) {}
  };

  const leaveAndSubmit = async () => {
    safeToCleanupRef.current = true;
    setLeavePromptOpen(false);
    try {
      if (submitRef.current) {
        await submitRef.current(true);
      } else {
        const payload = {
          autoSubmit: true,
          responses: questions.map((q) => ({
            questionId: q._id,
            selectedOptionKey: answers[q._id]?.selectedOptionKey || null
          }))
        };
        sendBeaconSubmit(payload);
        cleanupMediaAndFullscreen();
        navigate("/dashboard?tab=my", { replace: true });
      }
    } catch (_) {
      try {
        navigate("/dashboard?tab=my", { replace: true });
      } catch (_) {}
    }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const updateAnswer = (questionId, changes) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        selectedOptionKey: prev[questionId]?.selectedOptionKey || null,
        markedForReview: prev[questionId]?.markedForReview || false,
        ...changes
      }
    }));
  };

  const getStatusForQuestion = (qId) => {
    const a = answers[qId];
    if (!a) return "unanswered";
    if (a.markedForReview) return "review";
    if (a.selectedOptionKey) return "answered";
    return "unanswered";
  };
  // --- LOGIC END ---

  if (loading || !exam || !attempt || questions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center font-sans bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
        <p className="text-gray-500">Loading exam environment...</p>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentQuestion._id] || {};
  const timeText = secondsLeft != null ? formatTime(secondsLeft) : "--:--";
  const answeredCount = questions.filter((q) => answers[q._id]?.selectedOptionKey).length;
  const reviewCount = questions.filter((q) => answers[q._id]?.markedForReview).length;
  const remainingCount = questions.length - answeredCount;
  const candidateName = attempt.nameOnCertificate || attempt.userId?.name || "Candidate";

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-50 overflow-hidden font-sans select-none">
      
      {/* Header */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shrink-0 z-20 shadow-sm">
        <div className="flex items-center gap-4">
           <img src={logo} alt="Orivon" className="h-8 w-auto" />
           <div className="hidden md:block w-px h-6 bg-gray-200"></div>
           <div>
             <h1 className="text-gray-800 font-bold leading-tight">{exam.title}</h1>
             <p className="text-xs text-gray-500">Question {currentIndex + 1} of {questions.length}</p>
           </div>
        </div>

        <div className="flex items-center gap-6">
           <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-100 rounded-full">
              <ShieldCheck size={16} className="text-green-600" />
              <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Proctored</span>
           </div>
           
           <div className="text-right hidden sm:block">
              <p className="text-xs text-gray-500">Candidate</p>
              <p className="text-sm font-semibold text-gray-800">{candidateName}</p>
           </div>
           
           <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${secondsLeft < 300 ? "bg-red-50 border-red-200 text-red-700 animate-pulse" : "bg-gray-100 border-gray-200 text-gray-700"}`}>
              <Clock size={20} />
              <span className="font-mono text-xl font-bold">{timeText}</span>
           </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* Left Panel: Question Area */}
        <main className="flex-1 flex flex-col overflow-hidden relative">
          
          {/* Notifications / Warnings */}
          <div className="absolute top-0 left-0 right-0 z-10 px-6 pt-4 pointer-events-none">
             {/* Submitting Overlay - Shows on Strike 3 */}
             {submitting && (
               <div className="pointer-events-auto bg-gray-900/90 backdrop-blur text-white p-4 rounded-lg shadow-xl flex items-center justify-center gap-3 mx-auto max-w-sm animate-pulse">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span className="font-bold">Submitting Exam...</span>
               </div>
             )}

             {banner && !submitting && (
                <div className={`pointer-events-auto p-3 rounded-lg border shadow-lg flex items-center gap-3 mb-4 mx-auto max-w-2xl ${bannerType === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
                   {bannerType === 'error' ? <AlertCircle size={20}/> : <CheckCircle size={20}/>}
                   {banner}
                </div>
             )}
             
             {/* UPDATED WARNING UI: Handles Both Strike 1/2 (Normal) and Strike 3 (Lockout) */}
             {fullscreenWarning && !submitting && (
                <div className="pointer-events-auto bg-red-600 text-white p-4 rounded-lg shadow-xl flex items-center justify-between animate-bounce mx-auto max-w-2xl">
                   <div className="flex items-center gap-3">
                      <AlertTriangle size={24} />
                      <div>
                         <p className="font-bold">
                            {fullscreenExitCount >= 3 
                              ? `Maximum Violations (${fullscreenExitCount}/3)` 
                              : `Security Alert (Warning ${fullscreenExitCount}/3)`
                            }
                         </p>
                         <p className="text-sm text-red-100">
                           {fullscreenExitCount >= 3 
                             ? `Auto-submitting exam in ${fullscreenSecondsLeft}s...` 
                             : `Please return to fullscreen in ${fullscreenSecondsLeft}s or exam will be auto-submitted.`
                           }
                         </p>
                      </div>
                   </div>
                   
                   {/* HIDE BUTTON ON STRIKE 3 */}
                   {fullscreenExitCount < 3 && (
                     <button onClick={() => document.documentElement.requestFullscreen().catch(()=>{})} className="px-4 py-2 bg-white text-red-600 rounded-lg font-bold text-sm hover:bg-red-50">
                        Return Fullscreen
                     </button>
                   )}
                </div>
             )}
          </div>

          {/* Question Content */}
          <div ref={questionAreaRef} className="flex-1 overflow-y-auto p-6 md:p-10 pb-48">
             <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                   <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold uppercase tracking-wider rounded-md mb-4">
                     Question {currentIndex + 1}
                   </span>
                   <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-relaxed">
                      {currentQuestion.text}
                   </h2>
                </div>

                <div className="space-y-4">
                   {currentQuestion.options.map((opt) => {
                     const selected = currentAnswer.selectedOptionKey === opt.key;
                     return (
                        <div 
                           key={opt.key}
                           onClick={() => updateAnswer(currentQuestion._id, { selectedOptionKey: opt.key })}
                           className={`w-full text-left p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 flex items-start gap-4 group relative overflow-hidden
                             ${selected ? 'border-orange-500 bg-orange-50/50 shadow-sm' : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'}`}
                        >
                           <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors
                              ${selected ? 'border-orange-500 bg-orange-500 text-white' : 'border-gray-300 group-hover:border-orange-400'}`}>
                              {selected && <div className="w-2 h-2 bg-white rounded-full" />}
                           </div>
                           <div>
                              <span className={`font-bold mr-2 ${selected ? 'text-orange-700' : 'text-gray-500'}`}>{opt.key}.</span>
                              <span className={`${selected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>{opt.text}</span>
                           </div>
                        </div>
                     );
                   })}
                </div>
             </div>
          </div>

          {/* Bottom Control Bar Panel */}
          <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
              
              {/* LEGEND SECTION */}
              <div className="px-4 md:px-8 py-3 bg-gray-50/50 border-b border-gray-100 flex items-center justify-center gap-10 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  <div className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 rounded bg-green-500"></div> 
                    Answered <span className="text-gray-800 ml-1">{answeredCount}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 rounded bg-purple-500"></div> 
                    Review <span className="text-gray-800 ml-1">{reviewCount}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 rounded border border-gray-300 bg-white"></div> 
                    Left <span className="text-gray-800 ml-1">{remainingCount}</span>
                  </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 md:px-8 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button 
                       disabled={currentIndex === 0}
                       onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                       className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium text-sm transition-colors"
                    >
                       <ChevronLeft size={16}/> Prev
                    </button>
                    <button 
                      onClick={() => setAnswers(prev => { const c = {...prev}; delete c[currentQuestion._id]; return c; })}
                      className="text-gray-400 hover:text-gray-600 text-sm underline px-2"
                    >
                      Clear
                    </button>
                </div>

                <div className="flex items-center gap-3">
                    <button 
                       onClick={() => {
                          updateAnswer(currentQuestion._id, { markedForReview: !currentAnswer.markedForReview });
                          if (!currentAnswer.markedForReview) setCurrentIndex((i) => Math.min(questions.length - 1, i + 1));
                       }}
                       className={`px-4 py-2 rounded-lg border flex items-center gap-2 font-medium text-sm transition-colors
                          ${currentAnswer.markedForReview 
                             ? 'bg-purple-50 border-purple-200 text-purple-700' 
                             : 'border-gray-300 text-gray-600 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200'}`}
                    >
                       <Flag size={16}/> {currentAnswer.markedForReview ? "Marked" : "Review"}
                    </button>
                    
                    <button 
                       onClick={() => {
                          updateAnswer(currentQuestion._id, { markedForReview: false });
                          setCurrentIndex((i) => Math.min(questions.length - 1, i + 1));
                       }}
                       className="px-6 py-2 rounded-lg bg-gray-900 text-white hover:bg-orange-600 font-bold text-sm shadow-lg shadow-orange-500/20 transition-all flex items-center gap-2"
                    >
                       Save & Next <ChevronRight size={16}/>
                    </button>
                </div>
              </div>
          </div>
        </main>

        {/* Right Panel: Sidebar */}
        <aside className="w-72 md:w-80 bg-gray-50 border-l border-gray-200 flex flex-col shrink-0">
           
           {/* Camera Preview */}
           <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex justify-between items-center mb-2">
                 <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1"><Video size={14}/> Camera Feed</h3>
                 <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-100 animate-pulse">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full"></div> REC
                 </span>
              </div>
              <div className="aspect-video bg-black rounded-lg overflow-hidden border border-gray-300 relative">
                 <video ref={videoRef} className="w-full h-full object-cover transform scale-x-[-1]" autoPlay muted playsInline />
                 {cameraError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 text-white text-xs p-2 text-center">
                       {cameraError}
                    </div>
                 )}
              </div>
           </div>

           {/* Question Palette */}
           <div className="flex-1 overflow-y-auto p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1"><Grid size={14}/> Question Map</h3>
              
              <div className="grid grid-cols-5 gap-2">
                 {questions.map((q, idx) => {
                    const status = getStatusForQuestion(q._id);
                    let baseClass = "h-8 w-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all border";
                    let statusClass = "bg-white border-gray-300 text-gray-500 hover:border-gray-400"; // Unanswered
                    
                    if (status === "answered") statusClass = "bg-green-500 border-green-600 text-white shadow-sm";
                    if (status === "review") statusClass = "bg-purple-500 border-purple-600 text-white shadow-sm";
                    if (idx === currentIndex) statusClass += " ring-2 ring-offset-1 ring-gray-800";

                    return (
                       <button
                          key={q._id}
                          onClick={() => setCurrentIndex(idx)}
                          className={`${baseClass} ${statusClass}`}
                       >
                          {idx + 1}
                       </button>
                    );
                 })}
              </div>
           </div>

           {/* Submit Button */}
           <div className="p-4 border-t border-gray-200 bg-white">
              <button 
                 onClick={() => setShowConfirm(true)}
                 disabled={submitting || submitted}
                 className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-red-600 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                 {submitting ? "Submitting..." : submitted ? "Done" : "Submit Exam"}
              </button>
           </div>
        </aside>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
           <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
                 <AlertTriangle size={24} />
              </div>
              <h3 className="text-xl font-bold text-center text-gray-900 mb-2">Finish Exam?</h3>
              <p className="text-center text-gray-500 text-sm mb-6">
                 You have answered <strong>{answeredCount}</strong> out of <strong>{questions.length}</strong> questions. You cannot change answers after submitting.
              </p>
              <div className="flex gap-3">
                 <button 
                    onClick={() => setShowConfirm(false)}
                    className="flex-1 py-2.5 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50"
                 >
                    Cancel
                 </button>
                 <button 
                    onClick={async () => { setShowConfirm(false); await handleSubmit(false); }}
                    className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl font-bold hover:bg-red-600 shadow-lg shadow-red-500/20"
                 >
                    Yes, Submit
                 </button>
              </div>
           </div>
        </div>
      )}

      {/* Leave Prompt Modal */}
      {leavePromptOpen && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
           <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
              <div className="flex gap-4 items-center mb-4">
                 <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0 text-red-600">
                    <AlertCircle size={20} />
                 </div>
                 <h3 className="text-lg font-bold text-gray-900">Leave Exam?</h3>
              </div>
              
              <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                 If you leave this page, your current progress will be submitted automatically. Do you want to continue?
              </p>
              
              <div className="flex gap-3">
                 <button 
                    onClick={stayOnExam}
                    className="flex-1 py-2.5 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50"
                 >
                    Stay on Exam
                 </button>
                 <button 
                    onClick={leaveAndSubmit}
                    className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 shadow-lg shadow-red-500/20"
                 >
                    Leave & Submit
                 </button>
              </div>
           </div>
        </div>
      )}

    </div>
  );
}

export default ExamSession;